import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import * as SwitchPrimitive from '@radix-ui/react-switch'
import classNames from 'classnames'
import i18next from 'i18next'

import { selectCurrentProjectId } from '@store/slices/projects/selectors'
import { selectCanvasObject, updateOneCanvasObject } from '@store/slices/shapes/actions'
import {
  selectCanvasObjectsWithoutWorkArea,
  selectCanvasSelectedObjectId
} from '@store/slices/shapes/selectors'
import { setSlideObjectState } from '@store/slices/slides/actions'
import { selectCurrentSlideId, selectSlidesObjectState } from '@store/slices/slides/selectors'
import { useAppDispatch } from '@store/store'

import { updateShape } from '@/services/shape-service'

import Icon from '@components/icon/Icon'

import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

import './layers-dropdown.less'

const objectIcons: Record<string, string> = {
  textbox: 'font',
  circle: 'circle',
  polygon: 'polygon',
  table: 'table',
  chart: 'chart-bar'
}

const isCompactViewport = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(max-width: 640px)').matches

const LayersDropdown = () => {
  const dispatch = useAppDispatch()
  const objects = useSelector(selectCanvasObjectsWithoutWorkArea)
  const selectedObjectId = useSelector(selectCanvasSelectedObjectId)
  const selectedSlideId = useSelector(selectCurrentSlideId)
  const selectedProjectId = useSelector(selectCurrentProjectId)
  const slidesObjectState = useSelector(selectSlidesObjectState)

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setOpen(false)
  }, [selectedSlideId])

  useEffect(() => {
    if (!open) {
      setQuery('')
    }
  }, [open])

  const orderedObjects = useMemo(() => [...objects].reverse(), [objects])

  const filteredObjects = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) {
      return orderedObjects
    }

    return orderedObjects.filter(obj =>
      String(obj.name || '')
        .toLowerCase()
        .includes(term)
    )
  }, [orderedObjects, query])

  const selectObject = obj => {
    dispatch(selectCanvasObject(obj))

    if (isCompactViewport()) {
      setOpen(false)
    }
  }

  const toggleVisibility = (visible: boolean, obj) => {
    const currentSlideState = slidesObjectState?.[selectedSlideId]
    const nextSlideState = Array.isArray(currentSlideState) ? [...currentSlideState] : []
    const existingIndex = nextSlideState.findIndex(item => item.id === obj.id)
    const nextEntry = { id: obj.id, visibility: visible }

    if (existingIndex >= 0) {
      nextSlideState[existingIndex] = { ...nextSlideState[existingIndex], ...nextEntry }
    } else {
      nextSlideState.push(nextEntry)
    }

    dispatch(
      setSlideObjectState({
        ...slidesObjectState,
        [selectedSlideId]: nextSlideState
      })
    )
    dispatch(updateOneCanvasObject({ id: obj.id, changes: { visible } }))

    if (selectedProjectId && obj.pk) {
      updateShape({
        projectId: selectedProjectId,
        shapeId: obj.pk,
        payload: { visible }
      })
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="layers-trigger"
          id="header-toolbar-shapes-list-button"
          aria-label={i18next.t('editor.main.shapes-list.title')}
        >
          <Icon name="layer-group" size={0.85} />
          <span>{i18next.t('editor.main.shapes-list.title')}</span>
          <span className="layers-trigger-count">{objects.length}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="layers-popover"
        role="dialog"
        aria-label={i18next.t('editor.main.shapes-list.title')}
        onOpenAutoFocus={event => {
          if (isCompactViewport()) {
            return
          }

          event.preventDefault()
          searchInputRef.current?.focus()
        }}
      >
        <div className="layers-panel">
          <div className="layers-header">
            <span className="layers-title">{i18next.t('editor.main.shapes-list.title')}</span>
            <PopoverClose asChild>
              <button
                type="button"
                className="layers-close"
                aria-label={i18next.t('editor.main.shapes-list.close')}
              >
                <Icon name="times" size={0.8} />
              </button>
            </PopoverClose>
          </div>

          <div className="layers-search">
            <Icon name="search" size={0.8} className="layers-search-icon" />
            <input
              ref={searchInputRef}
              className="layers-search-input"
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder={i18next.t('editor.main.shapes-list.search-node')}
              aria-label={i18next.t('editor.main.shapes-list.search-node')}
            />
            {query && (
              <button
                type="button"
                className="layers-search-clear"
                onClick={() => setQuery('')}
                aria-label={i18next.t('editor.main.shapes-list.clear-search')}
              >
                <Icon name="times" size={0.65} />
              </button>
            )}
          </div>

          <div className="layers-list">
            {filteredObjects.length === 0 ? (
              <div className="layers-empty">
                <Icon name="layer-group" size={1.6} />
                <span>
                  {query
                    ? i18next.t('editor.main.shapes-list.no-results')
                    : i18next.t('editor.main.shapes-list.empty')}
                </span>
              </div>
            ) : (
              filteredObjects.map(obj => {
                const isSelected = selectedObjectId === obj.id
                const isVisible = obj.visible !== false

                return (
                  <div
                    key={obj.id}
                    className={classNames('layers-item', {
                      'layers-item-selected': isSelected
                    })}
                  >
                    <button
                      type="button"
                      className="layers-item-row"
                      onClick={() => selectObject(obj)}
                      aria-pressed={isSelected}
                    >
                      <span className="layers-item-icon">
                        <Icon name={objectIcons[obj.type] || 'image'} size={0.85} />
                      </span>
                      <span className="layers-item-name" title={obj.name}>
                        {obj.name}
                      </span>
                    </button>
                    <SwitchPrimitive.Root
                      checked={isVisible}
                      onCheckedChange={checked => toggleVisibility(checked, obj)}
                      className="layers-switch"
                      aria-label={
                        isVisible
                          ? i18next.t('editor.main.shapes-list.hide', { name: obj.name })
                          : i18next.t('editor.main.shapes-list.show', { name: obj.name })
                      }
                    >
                      <SwitchPrimitive.Thumb className="layers-switch-thumb" />
                    </SwitchPrimitive.Root>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default React.memo(LayersDropdown)
